import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Input, Spinner } from '@components/ui';
import { useRoles, useAllPermissions, useCreateRole, useUpdateRole, useDeleteRole } from '@hooks';
import { ProtectedRoute } from '@components/auth';

// Modal para crear/editar roles
function RoleModal({ isOpen, onClose, role, permissions, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    displayName: role?.displayName || '',
    description: role?.description || '',
    color: role?.color || '#64748b',
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // Actualizar estado cuando cambia el rol o se abre el modal
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: role?.name || '',
        displayName: role?.displayName || '',
        description: role?.description || '',
        color: role?.color || '#64748b',
      });
      setSelectedPermissions(role?.permissions?.map((rp) => rp.permissionId) || []);
    }
  }, [isOpen, role]);

  const groupedPermissions = useMemo(() => {
    if (!permissions?.grouped) return {};
    return permissions.grouped;
  }, [permissions]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      permissionIds: selectedPermissions,
    });
  };

  const togglePermission = (permId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permId) ? prev.filter((id) => id !== permId) : [...prev, permId]
    );
  };

  const toggleModule = (modulePerms) => {
    const modulePermIds = modulePerms.map((p) => p.id);
    const allSelected = modulePermIds.every((id) => selectedPermissions.includes(id));

    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((id) => !modulePermIds.includes(id)));
    } else {
      setSelectedPermissions((prev) => [...new Set([...prev, ...modulePermIds])]);
    }
  };

  // Obtener todos los IDs de permisos
  const allPermissionIds = useMemo(() => {
    if (!permissions?.grouped) return [];
    return Object.values(permissions.grouped).flat().map((p) => p.id);
  }, [permissions]);

  const allPermissionsSelected = allPermissionIds.length > 0 &&
    allPermissionIds.every((id) => selectedPermissions.includes(id));
  const somePermissionsSelected = allPermissionIds.some((id) => selectedPermissions.includes(id));

  const toggleAll = () => {
    if (allPermissionsSelected) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(allPermissionIds);
    }
  };

  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle = {
    background: 'white',
    borderRadius: '16px',
    width: '700px',
    maxHeight: '85vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
            {role ? 'Editar Rol' : 'Crear Nuevo Rol'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ padding: '24px' }}>
            {/* Información básica */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Identificador
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="CUSTOM_ROLE"
                  disabled={role?.isSystem}
                  required
                />
                {role?.isSystem && (
                  <span style={{ fontSize: '11px', color: '#f59e0b' }}>
                    Los roles del sistema no pueden cambiar su identificador
                  </span>
                )}
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Nombre visible
                </label>
                <Input
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="Mi Rol Personalizado"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Descripción
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del rol..."
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  style={{
                    width: '48px',
                    height: '40px',
                    padding: '4px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                />
              </div>
            </div>

            {/* Permisos por módulo */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>Permisos</h3>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    background: allPermissionsSelected ? '#667eea15' : '#f1f5f9',
                    border: allPermissionsSelected ? '1px solid #667eea' : '1px solid #e2e8f0',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: allPermissionsSelected ? '#667eea' : '#64748b',
                    transition: 'all 0.2s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allPermissionsSelected}
                    ref={(el) => el && (el.indeterminate = somePermissionsSelected && !allPermissionsSelected)}
                    onChange={toggleAll}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Seleccionar todo
                  <span style={{ color: '#94a3b8', fontWeight: '400' }}>
                    ({selectedPermissions.length}/{allPermissionIds.length})
                  </span>
                </label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {Object.entries(groupedPermissions).map(([module, perms]) => {
                  const modulePermIds = perms.map((p) => p.id);
                  const allSelected = modulePermIds.every((id) => selectedPermissions.includes(id));
                  const someSelected = modulePermIds.some((id) => selectedPermissions.includes(id));

                  return (
                    <div
                      key={module}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          background: '#f8fafc',
                          borderBottom: '1px solid #e2e8f0',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            checked={allSelected}
                            ref={(el) => el && (el.indeterminate = someSelected && !allSelected)}
                            onChange={() => toggleModule(perms)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                          <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>{module}</span>
                        </div>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          {modulePermIds.filter((id) => selectedPermissions.includes(id)).length}/{perms.length}
                        </span>
                      </div>
                      <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {perms.map((perm) => (
                          <label
                            key={perm.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              background: selectedPermissions.includes(perm.id) ? '#667eea15' : '#f1f5f9',
                              cursor: 'pointer',
                              fontSize: '13px',
                              border: selectedPermissions.includes(perm.id)
                                ? '1px solid #667eea'
                                : '1px solid transparent',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              style={{ display: 'none' }}
                            />
                            <span>{perm.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Guardando...' : role ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

RoleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  role: PropTypes.object,
  permissions: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

// Sección principal de roles
export default function RolesSection() {
  const { data: rolesData, isLoading: rolesLoading } = useRoles();
  const { data: permissionsData } = useAllPermissions();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const roles = rolesData?.data || [];
  const permissions = permissionsData?.data || {};

  const handleCreateRole = () => {
    setEditingRole(null);
    setIsModalOpen(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setIsModalOpen(true);
  };

  const handleSaveRole = async (data) => {
    try {
      if (editingRole) {
        await updateRole.mutateAsync({ id: editingRole.id, data });
      } else {
        await createRole.mutateAsync(data);
      }
      setIsModalOpen(false);
      setEditingRole(null);
    } catch (error) {
      console.error('Error saving role:', error);
    }
  };

  const handleDeleteRole = async (role) => {
    if (role.isSystem) {
      alert('No se pueden eliminar roles del sistema');
      return;
    }
    if (role._count?.users > 0) {
      alert(`No se puede eliminar: ${role._count.users} usuario(s) tienen este rol asignado`);
      return;
    }
    if (window.confirm(`¿Eliminar el rol "${role.displayName}"?`)) {
      try {
        await deleteRole.mutateAsync(role.id);
      } catch (error) {
        console.error('Error deleting role:', error);
      }
    }
  };

  if (rolesLoading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <ProtectedRoute permission="admin.gestionar_roles">
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>
              Gestión de Roles
            </h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Configura los permisos de cada rol del sistema
            </p>
          </div>
          <Button variant="primary" onClick={handleCreateRole}>
            + Nuevo Rol
          </Button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {roles.map((role) => (
            <div
              key={role.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                background: '#fafafa',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: role.color,
                  }}
                />
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>{role.displayName}</span>
                    {role.isSystem && (
                      <span
                        style={{
                          fontSize: '10px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          background: '#f59e0b20',
                          color: '#d97706',
                          fontWeight: '500',
                        }}
                      >
                        Sistema
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0' }}>
                    {role.description || 'Sin descripción'}
                  </p>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '11px', color: '#94a3b8' }}>
                    <span>{role._count?.users || 0} usuario(s)</span>
                    <span>{role.permissions?.length || 0} permiso(s)</span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="secondary" size="sm" onClick={() => handleEditRole(role)}>
                  Editar
                </Button>
                {!role.isSystem && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteRole(role)}
                    disabled={role._count?.users > 0}
                  >
                    Eliminar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {roles.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            No hay roles configurados. Ejecuta el seeder para crear los roles del sistema.
          </div>
        )}
      </Card>

      <RoleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRole(null);
        }}
        role={editingRole}
        permissions={permissions}
        onSave={handleSaveRole}
        isLoading={createRole.isPending || updateRole.isPending}
      />
    </ProtectedRoute>
  );
}
